import React, { useEffect, useMemo, useState } from 'react';
import { Play, AlertTriangle, RotateCcw, ArrowLeft, ArrowRight } from 'lucide-react';
import { buildingChecksApi, AssessmentTemplates } from '../../../../../api/buildingChecksApi';
import { DateInterval, formatDateForDisplay, formatDateOnlyForStorage } from '../../../../../Helpers/DateUtils';
import { useUserNames } from '../../../../../Helpers/useUserNames';
import {
    ASSESSMENT_GROUP_KEYS,
    ASSESSMENT_GROUPS,
    AssessmentGroupKey,
    AssessmentScheduleDraft,
    AssessmentScheduleDrafts,
    assessmentTemplateId,
    resolveAssessmentSchedule
} from '../../../../../Helpers/assessmentGroups';
import { BuildingCheck, formatInterval } from '../../../../BuildingChecks/buildingChecksUtils';
import { DueDateEditor, IntervalEditor } from '../../../components/AssessmentScheduleEditors';
import type { ActivationAssessment } from '../../hooks/useActivatePlan';
import type { PlanDiff } from '../../utils/planDiff';
import { PlanDiffView } from '../ui/PlanDiffView';

interface ActivatePlanModalProps {
    buildingKey: string;
    /** Live components per Skickbedömning group; a group without any is not shown and not scheduled */
    assessmentGroupCounts: Partial<Record<AssessmentGroupKey, number>>;
    hasExistingPlan: boolean;
    excludedCount: number;
    totalCount: number;
    onConfirm: (assessment: ActivationAssessment) => void;
    onCancel: () => void;
    activating: boolean;
    /** The draft against the active plan, reviewed in step 1; null without an active plan (no step 1) */
    diff?: PlanDiff | null;
    yearsToShow?: number;
    onYearsToShowChange?: (years: number) => void;
}

type AssessmentData =
    | { status: 'loading' | 'error' }
    | { status: 'ready'; instances: Record<string, BuildingCheck>; templates: AssessmentTemplates };

const NO_ASSESSMENT: AssessmentData = { status: 'ready', instances: {}, templates: {} };

/**
 * Modal for confirming maintenance plan activation.
 *
 * The Skickbedömning schedule is set here and nowhere else on the draft: one row per
 * group the building has live components in. Edits are held in the modal, so cancelling
 * discards them, and confirming hands them to the activation's atomic update.
 */
export const ActivatePlanModal: React.FC<ActivatePlanModalProps> = ({
    buildingKey,
    assessmentGroupCounts,
    hasExistingPlan,
    excludedCount,
    totalCount,
    onConfirm,
    onCancel,
    activating,
    diff = null,
    yearsToShow = 10,
    onYearsToShowChange = () => undefined
}) => {
    // With an active plan the changes are reviewed first ("Granska ändringar"), then confirmed
    const hasReview = hasExistingPlan && !!diff;
    const [step, setStep] = useState<'review' | 'confirm'>(hasReview ? 'review' : 'confirm');
    const isEmptyPlan = excludedCount === totalCount && totalCount > 0;

    const groups = useMemo(
        () => ASSESSMENT_GROUP_KEYS.filter(group => (assessmentGroupCounts[group] ?? 0) > 0),
        [assessmentGroupCounts]
    );
    const hasGroups = groups.length > 0;

    // Loaded when the modal opens, so templates created and assessments completed
    // while the draft was open are what the user confirms
    const [assessment, setAssessment] = useState<AssessmentData>(hasGroups ? { status: 'loading' } : NO_ASSESSMENT);
    const [staged, setStaged] = useState<AssessmentScheduleDrafts>({});
    const { userNames, loadUserNames } = useUserNames();

    useEffect(() => {
        if (!hasGroups) return;
        let cancelled = false;
        Promise.all([
            buildingChecksApi.loadBuildingCheckInstances(buildingKey),
            buildingChecksApi.loadAssessmentTemplates()
        ]).then(([instances, templates]) => {
            if (cancelled) return;
            setAssessment({ status: 'ready', instances, templates });
            const uids = groups
                .map(group => instances[assessmentTemplateId(group)]?.lastCompletedBy)
                .filter((uid): uid is string => !!uid);
            if (uids.length > 0) loadUserNames(uids);
        }).catch(error => {
            console.error('Error loading Skickbedömning schedule:', error);
            if (!cancelled) setAssessment({ status: 'error' });
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildingKey]);

    const ready = assessment.status === 'ready' ? assessment : null;
    const templatesMissing = !!ready && ASSESSMENT_GROUP_KEYS.some(group => !ready.templates[group]);
    const today = formatDateOnlyForStorage(new Date());

    // A group's entry is created on its first edit, seeded with the instance's stored
    // interval override so that editing only the date never clears it
    const stage = (group: AssessmentGroupKey, mutate: (entry: AssessmentScheduleDraft) => void) => {
        setStaged(prev => {
            const instance = ready?.instances[assessmentTemplateId(group)];
            const entry: AssessmentScheduleDraft = prev[group]
                ? { ...prev[group] }
                : instance?.customInterval ? { customInterval: instance.customInterval } : {};
            mutate(entry);
            return { ...prev, [group]: entry };
        });
    };
    const stageInterval = (group: AssessmentGroupKey, interval: DateInterval | null) => stage(group, entry => {
        if (interval) {
            entry.customInterval = interval;
        } else {
            delete entry.customInterval;
        }
    });
    const stageDate = (group: AssessmentGroupKey, date: string) => stage(group, entry => {
        entry.nextDate = date;
    });

    const handleConfirm = () => {
        if (!ready) return;
        onConfirm({ staged, instances: ready.instances, templates: ready.templates });
    };

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget && !activating) {
                        onCancel();
                    }
                }}
            >
                <div className={`modal-dialog modal-dialog-centered ${step === 'review' ? 'modal-xl' : ''}`}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <Play size={20} className="me-2 text-success" />
                                Aktivera underhållsplan
                                {hasReview && (
                                    <span className="text-muted small fw-normal ms-2">
                                        {step === 'review' ? 'Steg 1 av 2: Granska ändringar' : 'Steg 2 av 2: Bekräfta'}
                                    </span>
                                )}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onCancel}
                                aria-label="Stäng"
                                disabled={activating}
                            />
                        </div>
                        {step === 'review' && diff ? (
                            <div className="modal-body activate-plan-diff">
                                <p>
                                    Så här skiljer sig utkastet från den aktiva planen. Inget ändras förrän du aktiverar i nästa steg.
                                </p>
                                <PlanDiffView
                                    diff={diff}
                                    yearsToShow={yearsToShow}
                                    onYearsToShowChange={onYearsToShowChange}
                                />
                            </div>
                        ) : (
                            <div className="modal-body">
                                {hasExistingPlan ? (
                                    <p>
                                        Aktivera denna underhållsplan? Den ersätter din nuvarande
                                        aktiva plan som arkiveras.
                                    </p>
                                ) : (
                                    <p>Aktivera denna underhållsplan?</p>
                                )}

                                {isEmptyPlan && (
                                    <div className="alert alert-warning d-flex align-items-start mb-0">
                                        <AlertTriangle size={20} className="me-2 flex-shrink-0 mt-1" />
                                        <div>
                                            <strong>Varning:</strong> Alla komponenter är exkluderade.
                                            Planen blir tom.
                                        </div>
                                    </div>
                                )}

                                {hasGroups && (
                                    <div className="activate-assessment-section">
                                        <h6 className="activate-assessment-title">Skickbedömning</h6>

                                        {assessment.status === 'loading' && (
                                            <div className="d-flex align-items-center gap-2 text-muted small">
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                Hämtar schema...
                                            </div>
                                        )}

                                        {assessment.status === 'error' && (
                                            <div className="alert alert-danger py-2 mb-0">
                                                Schemat för skickbedömning kunde inte hämtas. Stäng och försök igen.
                                            </div>
                                        )}

                                        {ready && templatesMissing && (
                                            <div className="alert alert-warning py-2 mb-0">
                                                Mallar för skickbedömning saknas – skapas under Konfigurationsstatus av en administratör.
                                                Planen kan aktiveras, men skickbedömning schemaläggs inte förrän mallarna finns.
                                            </div>
                                        )}

                                        {ready && !templatesMissing && (
                                            <>
                                                {groups.map(group => {
                                                    const instance = ready.instances[assessmentTemplateId(group)];
                                                    const template = ready.templates[group]!;
                                                    const schedule = resolveAssessmentSchedule({
                                                        group,
                                                        staged: staged[group],
                                                        instance,
                                                        template,
                                                        today
                                                    })!;
                                                    const count = assessmentGroupCounts[group] ?? 0;
                                                    const completedByName = instance?.lastCompletedBy ? userNames[instance.lastCompletedBy] : null;

                                                    return (
                                                        <div key={group} className="activate-assessment-group">
                                                            <div>
                                                                <span className="fw-semibold">{ASSESSMENT_GROUPS[group].label}</span>
                                                                <span className="text-muted small ms-2">{count} {count === 1 ? 'komponent' : 'komponenter'}</span>
                                                            </div>
                                                            <div className="activate-assessment-fields">
                                                                <div>
                                                                    <div className="assessment-schedule-label">Intervall</div>
                                                                    <IntervalEditor
                                                                        interval={schedule.interval}
                                                                        disabled={activating}
                                                                        onCommit={interval => stageInterval(group, interval)}
                                                                    />
                                                                    <div className="small text-muted mt-1">
                                                                        Standard: {formatInterval(template.interval)}
                                                                    </div>
                                                                    {schedule.hasCustomInterval && !activating && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link btn-sm p-0 assessment-schedule-reset"
                                                                            onClick={() => stageInterval(group, null)}
                                                                        >
                                                                            <RotateCcw size={12} className="me-1" />
                                                                            Återställ till standard
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <div className="assessment-schedule-label">{instance ? 'Nästa bedömning' : 'Första bedömning'}</div>
                                                                    <DueDateEditor
                                                                        value={schedule.nextDueDate}
                                                                        disabled={activating}
                                                                        onCommit={date => stageDate(group, date)}
                                                                    />
                                                                    {instance && (
                                                                        <div className="small text-muted mt-1">
                                                                            {instance.lastCompletedAt
                                                                                ? `Senast utförd ${formatDateForDisplay(instance.lastCompletedAt)}${completedByName ? ` av ${completedByName}` : ''}`
                                                                                : 'Aldrig utförd'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div className="text-muted small mt-2">
                                                    Schemaläggs när planen aktiveras. Bedömningen utförs i appen.
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onCancel}
                                disabled={activating}
                            >
                                Avbryt
                            </button>
                            {step === 'review' ? (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => setStep('confirm')}
                                >
                                    Nästa
                                    <ArrowRight size={16} className="ms-2" />
                                </button>
                            ) : (
                                <>
                                    {hasReview && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setStep('review')}
                                            disabled={activating}
                                        >
                                            <ArrowLeft size={16} className="me-2" />
                                            Tillbaka
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={handleConfirm}
                                        disabled={activating || !ready}
                                    >
                                        {activating ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Aktiverar...
                                            </>
                                        ) : (
                                            <>
                                                <Play size={16} className="me-2" />
                                                Aktivera
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
